import {
  AppCard,
  Button,
  FormTextField,
  Icon,
  RouteLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiEmailOutline } from "@mdi/js";
import { Box, Container, Divider, Typography } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EquinorLogo } from "../../../../../components/atoms/EquinorLogo/EquinorLogo";
import {
  Recaptcha,
  type RecaptchaRef,
} from "../../../../../components/atoms/Recaptcha/Recaptcha";
import { config } from "../../../../../config";
import {
  type ResendConfirmationFormData,
  resendConfirmationSchema,
} from "../-schemas/resendConfirmationSchema";

const RATE_LIMIT_MS = 60000; // 1 minute between requests

const RouteComponent = () => {
  const [loading, setLoading] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(resendConfirmationSchema),
  });

  const onSubmit: SubmitHandler<ResendConfirmationFormData> = async (
    values,
  ) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmitTime < RATE_LIMIT_MS) {
      enqueueSnackbar(t("auth.resendConfirmation.rateLimitMessage"), {
        variant: "warning",
      });
      return;
    }

    const token = recaptchaRef.current?.getValue();
    if (
      !token &&
      !(window.environment === "test" || window.environment === "development")
    ) {
      enqueueSnackbar(t("auth.signIn.invalidBotVerificationMessage"), {
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const bodyFormData = new FormData();
      bodyFormData.append("user[email]", values.email);
      bodyFormData.append("g-recaptcha-response", token ?? "");

      const response = await axios.postForm(
        "/users/confirmation",
        bodyFormData,
        {
          xsrfCookieName: config.csrfTokenCookieName,
          xsrfHeaderName: "x-csrf-token",
        },
      );

      if (response.status === 200) {
        enqueueSnackbar(t("auth.resendConfirmation.successMessage"), {
          variant: "success",
        });
        navigate({
          to: "/users/sign_in",
        });
      }
      setLastSubmitTime(now);
    } catch {
      enqueueSnackbar(t("common.genericError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
      // Reset reCAPTCHA after submission
      recaptchaRef.current?.reset();
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "0px !important",
      }}
    >
      <AppCard slotProps={{ root: { sx: { padding: 3 } } }}>
        <EquinorLogo />
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography component="h1" sx={{ textAlign: "center" }} variant="h5">
            {t("auth.resendConfirmation.title")}
          </Typography>
        </Box>
        <form
          name="resendConfirmationForm"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormTextField
              control={control}
              fullWidth
              name="email"
              onChange={(event) => {
                const normalizedValue = event.target.value.trim().toLowerCase();
                if (event.target.value !== normalizedValue) {
                  setValue("email", normalizedValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
              placeholder={t("common.email")}
              required
              slotProps={{
                input: {
                  startAdornment: (
                    <Icon
                      icon={mdiEmailOutline}
                      sx={{
                        color: (currentTheme) =>
                          currentTheme.vars.palette.action.active,
                        marginRight: 1,
                      }}
                    />
                  ),
                },
              }}
              type="email"
            />

            <Recaptcha ref={recaptchaRef} />

            <Button
              fullWidth
              loading={loading}
              loadingPosition="center"
              type="submit"
              variant="contained"
            >
              {t("auth.resendConfirmation.submitButton")}
            </Button>
          </Box>
        </form>
        <Divider sx={{ margin: "8px 0px" }} />
        <Box
          sx={{
            alignItems: "left",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <RouteLink to="/users/sign_in">{t("common.signIn")}</RouteLink>
          <RouteLink to="/users/password/new">
            {t("auth.signIn.forgotPasswordLink")}
          </RouteLink>
        </Box>
      </AppCard>
    </Container>
  );
};

export const Route = createFileRoute("/_public/_auth/users/confirmation/new")({
  component: RouteComponent,
});
